import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Sparkles } from 'lucide-react';
import { UserAwarePricingData } from '@/hooks/useUserAwarePricing';

interface PlanSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pricingData: UserAwarePricingData;
  onPlanSelected: (plan: 'monthly' | 'yearly') => void;
}

export const PlanSelector = ({ open, onOpenChange, pricingData, onPlanSelected }: PlanSelectorProps) => {
  const { t } = useTranslation();
  const monthlyPrice = pricingData.pricing?.monthly;
  const yearlyPrice = pricingData.pricing?.yearly;

  const handlePlanClick = (plan: 'monthly' | 'yearly') => {
    onPlanSelected(plan);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4 bg-gradient-to-r from-newsletter-primary/10 to-newsletter-secondary/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-newsletter-primary/20 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-newsletter-primary" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold text-newsletter-primary">
                Escolha seu plano
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                🎁 14 dias grátis para testar • Cancele quando quiser
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 grid gap-4 md:grid-cols-2">
          {/* Monthly Plan */}
          <div 
            onClick={() => handlePlanClick('monthly')}
            className="border-2 border-muted hover:border-newsletter-primary/50 rounded-xl p-6 cursor-pointer transition-all hover:shadow-newsletter group"
          >
            <div className="text-center space-y-4">
              <div>
                <h3 className="text-xl font-bold">Plano Mensal</h3>
                <p className="text-sm text-muted-foreground mt-1">Flexibilidade total</p>
              </div>

              <div className="py-4">
                <div className="text-4xl font-bold text-newsletter-primary">
                  {pricingData.symbol}{monthlyPrice?.price}
                  <span className="text-base text-muted-foreground font-normal">/mês</span>
                </div>
              </div>

              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>14 dias de trial grátis</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>Feed personalizado diário</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>Cancele a qualquer momento</span>
                </li>
              </ul>

              <Button 
                variant="outline" 
                className="w-full group-hover:border-newsletter-primary group-hover:bg-newsletter-primary/5"
              >
                Escolher Mensal
              </Button>
            </div>
          </div>

          {/* Yearly Plan */}
          <div 
            onClick={() => handlePlanClick('yearly')}
            className="border-2 border-newsletter-primary rounded-xl p-6 cursor-pointer transition-all shadow-newsletter group relative overflow-hidden"
          >
            <Badge className="absolute top-4 right-4 bg-green-600 text-white">
              50% OFF
            </Badge>

            <div className="text-center space-y-4">
              <div>
                <h3 className="text-xl font-bold">Plano Anual</h3>
                <p className="text-sm text-newsletter-primary mt-1 font-medium">⭐ Mais popular</p>
              </div>

              <div className="py-4">
                <div className="text-4xl font-bold text-newsletter-primary">
                  {pricingData.symbol}{yearlyPrice?.price}
                  <span className="text-base text-muted-foreground font-normal">/ano</span>
                </div>
                <div className="text-sm text-green-600 font-medium mt-2">
                  {pricingData.symbol}{yearlyPrice?.monthlyEquivalent}/mês • Economia de 50%
                </div>
              </div>

              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>14 dias de trial grátis</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>Feed personalizado diário</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>Cancele a qualquer momento</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="font-semibold text-newsletter-primary">Economize 50% no ano</span>
                </li>
              </ul>

              <Button 
                className="w-full bg-newsletter-primary hover:bg-newsletter-primary/90"
              >
                Escolher Anual
              </Button>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6">
          <p className="text-xs text-center text-muted-foreground">
            {t('funnel.plans.securePayment')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
